import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { BrowserProfileEditor } from '@/components/browser/BrowserProfileEditor';
import { BrowserProfileImport } from '@/components/browser/BrowserProfileImport';
import { PlayConsoleShare } from '@/components/browser/PlayConsoleShare';
import {
  listWorkspaces, createWorkspace, renameWorkspace, deleteWorkspace,
  listProfiles, deleteProfile, checkProfile, type BrowserProfile,
} from '@/api/browserProfiles';
import {
  listLiveAccounts, listLiveSessions, openLiveSession, openProfileLiveSession, type LiveAccount,
} from '@/api/playLive';
import { useAuthStore } from '@/store/auth';

// Browser profiles of free use: Google accounts with a ShardX profile on the
// VPS, grouped into workspaces. Play publishing accounts stay in First
// publication; they show here read-only so every profile is in one view.
// Super admins manage everything; the `browser` addon adds, edits, opens and
// checks profiles, and opens the Play accounts, but deletes nothing.

const SESSION_CLS: Record<string, string> = {
  ok: 'bg-green-500/15 text-green-400',
  expired: 'bg-amber-500/15 text-amber-400',
  locked: 'bg-red-500/15 text-red-400',
  unknown: 'bg-surface-muted text-content-muted',
};

const PLAY_TAB = 'play';
const inputCls = 'w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm';

function ago(ts: number | null): string {
  if (!ts) return 'never';
  const m = Math.floor((Date.now() / 1000 - ts) / 60);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (m < 48 * 60) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

function SessionPill({ status }: { status: string }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${SESSION_CLS[status] ?? SESSION_CLS.unknown}`}>
      {status}
    </span>
  );
}

export function BrowserProfiles() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isSuper = useAuthStore((s) => s.admin?.role === 'super_admin');
  // Linking a shared console makes a Play account: administrators and developers.
  const canLink = useAuthStore((s) => s.admin?.role === 'super_admin' || !!s.admin?.addons?.includes('developer'));
  const [sharing, setSharing] = useState<number | null>(null);
  const [tab, setTab] = useState<string>('');
  const [editing, setEditing] = useState<BrowserProfile | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [wsDialog, setWsDialog] = useState<{ id: number | null; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const wsQ = useQuery({ queryKey: ['browser-workspaces'], queryFn: listWorkspaces });
  const workspaces = wsQ.data ?? [];
  // Status changes come from the runner, so the list keeps itself fresh.
  const profilesQ = useQuery({ queryKey: ['browser-profiles'], queryFn: listProfiles, refetchInterval: 15_000 });
  const playQ = useQuery({ queryKey: ['live-accounts'], queryFn: listLiveAccounts, enabled: tab === PLAY_TAB });
  const liveQ = useQuery({ queryKey: ['live-sessions'], queryFn: listLiveSessions, refetchInterval: 10_000 });
  const openLive = (liveQ.data ?? []).filter((l) => l.status !== 'closed');
  const liveByProfile = new Map(openLive.filter((l) => l.profile_id).map((l) => [l.profile_id!, l]));
  const liveByAccount = new Map(openLive.filter((l) => l.account_id).map((l) => [l.account_id!, l]));

  // Land on the first workspace once they load.
  useEffect(() => {
    if (!tab && workspaces.length) setTab(String(workspaces[0]!.id));
  }, [tab, workspaces]);

  const wsId = tab && tab !== PLAY_TAB ? Number(tab) : null;
  const workspace = workspaces.find((w) => w.id === wsId) ?? null;
  const profiles = (profilesQ.data ?? []).filter((p) => p.workspace_id === wsId);

  const fail = (e: any) => setError(e?.message ?? 'Something went wrong');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['browser-profiles'] });
    qc.invalidateQueries({ queryKey: ['browser-workspaces'] });
  };

  const wsMut = useMutation({
    mutationFn: (d: { id: number | null; name: string }) => (d.id ? renameWorkspace(d.id, d.name) : createWorkspace(d.name)),
    onSuccess: (r, d) => { refresh(); setWsDialog(null); if (!d.id) setTab(String((r as { id: number }).id)); },
    onError: fail,
  });
  const wsDeleteMut = useMutation({
    mutationFn: (id: number) => deleteWorkspace(id),
    onSuccess: () => { refresh(); setTab(''); },
    onError: fail,
  });
  const checkMut = useMutation({ mutationFn: (id: number) => checkProfile(id), onSuccess: refresh, onError: fail });
  const deleteMut = useMutation({ mutationFn: (id: number) => deleteProfile(id), onSuccess: refresh, onError: fail });
  const liveMut = useMutation({
    mutationFn: (t: { profile?: number; account?: number }) => (t.profile ? openProfileLiveSession(t.profile) : openLiveSession(t.account!)),
    onSuccess: (s) => navigate(`/play/live/${s.id}`),
    onError: fail,
  });

  const browserCell = (id: number, kind: 'profile' | 'account', bound: boolean) => {
    const open = kind === 'profile' ? liveByProfile.get(id) : liveByAccount.get(id);
    if (open) {
      return (
        <span className="flex flex-col gap-1 items-start">
          <span className="text-green-400 text-xs">{open.status} · {open.admin_email}</span>
          <Button size="sm" variant="secondary" onClick={() => navigate(`/play/live/${open.id}`)}>View</Button>
        </span>
      );
    }
    return (
      <Button size="sm" variant="secondary" disabled={!bound}
        loading={liveMut.isPending && (liveMut.variables?.profile === id || liveMut.variables?.account === id)}
        title={bound ? 'Open this profile on the VPS and drive it from here' : 'Save a proxy first: the runner has not created the profile yet'}
        onClick={() => { setError(null); liveMut.mutate(kind === 'profile' ? { profile: id } : { account: id }); }}>
        Open
      </Button>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-semibold text-content">Browser profiles</h1>
        <Button size="sm" variant="secondary" onClick={() => setWsDialog({ id: null, name: '' })}>New workspace</Button>
      </div>
      <p className="text-sm text-content-muted mb-5">
        Google accounts with their own anti-detect profile on the VPS, for any use outside Play publishing. Save a
        proxy and the runner creates the profile, signs in and checks the session every few days; Open drives the
        browser from here. A profile with the Firebase scheme is offered under “New Firebase project” in Apps and Apps Setup.
      </p>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400">{error}</div>
      )}
      {notice && (
        <div className="mb-3 px-3 py-2 rounded-md bg-green-500/10 border border-green-500/30 text-sm text-green-400">{notice}</div>
      )}

      <div className="flex flex-wrap items-center gap-1 mb-4 border-b border-border">
        {workspaces.map((w) => (
          <button key={w.id} onClick={() => setTab(String(w.id))}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === String(w.id) ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
            {w.name} <span className="text-xs text-content-muted">{w.profiles}</span>
          </button>
        ))}
        <button onClick={() => setTab(PLAY_TAB)}
          className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === PLAY_TAB ? 'border-primary text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
          First publication <span className="text-xs text-content-muted">read-only</span>
        </button>
      </div>

      {!wsQ.isLoading && workspaces.length === 0 && tab !== PLAY_TAB && (
        <div className="bg-surface border border-border rounded-lg p-6 text-center text-sm text-content-muted">
          No workspaces yet. Create one (for example “Firebase”) and add profiles to it.
        </div>
      )}

      {workspace && (
        <>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setEditing('new')}>Add profile</Button>
              <Button size="sm" variant="secondary" onClick={() => { setError(null); setNotice(null); setImporting(true); }}>Import from file</Button>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => setWsDialog({ id: workspace.id, name: workspace.name })}>Rename</Button>
              {isSuper && (
              <Button size="sm" variant="secondary" disabled={profiles.length > 0}
                title={profiles.length ? 'Move or delete its profiles first' : undefined}
                onClick={() => { setError(null); wsDeleteMut.mutate(workspace.id); }}>
                Delete workspace
              </Button>
              )}
            </div>
          </div>
          <DataTable<BrowserProfile>
            loading={profilesQ.isLoading}
            data={profiles}
            keyExtractor={(p) => String(p.id)}
            columns={[
              {
                key: 'name', label: 'Profile',
                render: (p) => p.deleted_at
                  ? <span className="text-content-muted line-through">{p.name}</span>
                  : <button className="text-accent hover:underline" onClick={() => setEditing(p)}>{p.name}</button>,
              },
              { key: 'google_email', label: 'Google account', render: (p) => <span className="text-content-muted">{p.google_email ?? '—'}</span> },
              {
                key: 'scheme', label: 'Scheme',
                render: (p) => p.scheme === 'firebase'
                  ? (
                    <span className="flex flex-col gap-0.5 items-start">
                      <span className="text-content-muted">Firebase · {p.firebase_projects}/{p.firebase_max_projects}</span>
                      {(p.firebase_apps ?? []).map((a) => (
                        <span
                          key={a.bundle_id}
                          className={`font-mono text-xs ${a.error ? 'text-red-500' : a.state === 'ready' ? 'text-content' : 'text-content-subtle'}`}
                          title={a.error ?? (a.project_id ? `Firebase project ${a.project_id}` : undefined)}
                        >
                          {a.bundle_id}
                          {a.state !== 'ready' && <span className="font-sans"> · {a.error ? 'failed' : `setting up (${a.state})`}</span>}
                        </span>
                      ))}
                    </span>
                  )
                  : <span className="text-content-muted">—</span>,
              },
              {
                key: 'shardx_profile_id', label: 'ShardX profile',
                render: (p) => p.shardx_profile_id
                  ? <span className="text-content-muted font-mono text-xs">{p.shardx_profile_id.slice(0, 12)}…</span>
                  : <span className="text-content-muted text-xs">{p.proxy_label ? 'being created…' : 'save a proxy'}</span>,
              },
              {
                key: 'session_status', label: 'Session',
                render: (p) => (
                  <span className="flex flex-col gap-1 items-start">
                    <SessionPill status={p.session_status} />
                    <span className="text-xs text-content-muted">
                      {p.check_requested_at ? 'check queued' : `checked ${ago(p.last_keepalive_at)}`}
                    </span>
                  </span>
                ),
              },
              {
                key: 'factors', label: '2FA',
                render: (p) => (
                  <span className={p.has_totp && p.backup_codes_left <= 3 ? 'text-amber-400 text-xs' : 'text-content-muted text-xs'}>
                    {p.has_totp ? `TOTP · ${p.backup_codes_left} codes` : 'not set up'}
                  </span>
                ),
              },
                      {
                key: 'play', label: 'Play Console',
                render: (p) => (p.deleted_at ? null : (
                  <button className="text-xs text-accent hover:underline text-left" onClick={() => setSharing(p.id)}>
                    {p.play_account_id ? `→ ${p.play_account_name}`
                      : p.play_link_requested_at || p.play_link_started_at ? 'checking invite…'
                        : p.play_link?.developers.length ? 'invite accepted — link it' : 'Share…'}
                  </button>
                )),
              },
              { key: 'live', label: 'Browser', render: (p) => (p.deleted_at ? null : browserCell(p.id, 'profile', !!p.shardx_profile_id)) },
              {
                key: 'actions', label: '',
                render: (p) => p.deleted_at
                  ? <span className="text-xs text-content-muted">deleting from the VPS…</span>
                  : (
                    <span className="flex justify-end gap-2">
                      <Button size="sm" variant="secondary" disabled={!p.shardx_profile_id}
                        loading={checkMut.isPending && checkMut.variables === p.id}
                        onClick={() => { setError(null); checkMut.mutate(p.id); }}>
                        Check now
                      </Button>
                      {isSuper && (
                      <Button size="sm" variant="secondary"
                        onClick={() => {
                          if (confirm(`Delete ${p.name}? Its ShardX profile with the signed-in session is removed from the VPS too.`)) {
                            setError(null); deleteMut.mutate(p.id);
                          }
                        }}>
                        Delete
                      </Button>
                      )}
                    </span>
                  ),
              },
            ]}
          />
        </>
      )}

      {tab === PLAY_TAB && (
        <>
          <p className="text-sm text-content-muted mb-3">
            Play publishing accounts. Edit them in <Link to="/play/publish" className="text-accent hover:underline">First publication → Console access</Link>.
          </p>
          <DataTable<LiveAccount>
            loading={playQ.isLoading}
            data={playQ.data ?? []}
            keyExtractor={(a) => String(a.id)}
            columns={[
              { key: 'name', label: 'Account', render: (a) => <span className="text-content">{a.name}</span> },
              { key: 'google_email', label: 'Google account', render: (a) => <span className="text-content-muted">{a.google_email ?? '—'}</span> },
              { key: 'shardx_profile_id', label: 'ShardX profile', render: (a) => <span className="text-content-muted font-mono text-xs">{a.shardx_profile_id.slice(0, 12)}…</span> },
              { key: 'session_status', label: 'Session', render: (a) => <SessionPill status={a.session_status} /> },
              { key: 'live', label: 'Browser', render: (a) => browserCell(a.id, 'account', true) },
            ]}
          />
        </>
      )}

      <PlayConsoleShare
        profile={(profilesQ.data ?? []).find((p) => p.id === sharing) ?? null}
        canLink={canLink}
        onClose={() => setSharing(null)}
      />

      <BrowserProfileEditor profile={editing} workspaceId={wsId} workspaces={workspaces} onClose={() => setEditing(null)} />

      <BrowserProfileImport
        open={importing}
        workspace={workspace}
        onClose={() => setImporting(false)}
        onImported={(n) => setNotice(`Imported ${n} profile${n === 1 ? '' : 's'}. The runner is creating each ShardX profile on its proxy — watch the Session column.`)}
      />

      <Modal isOpen={!!wsDialog} onClose={() => setWsDialog(null)} title={wsDialog?.id ? 'Rename workspace' : 'New workspace'}>
        <input
          className={`${inputCls} mb-3`}
          autoFocus
          value={wsDialog?.name ?? ''}
          placeholder="e.g. Firebase"
          onChange={(e) => setWsDialog((d) => (d ? { ...d, name: e.target.value } : d))}
          onKeyDown={(e) => { if (e.key === 'Enter' && wsDialog?.name.trim()) wsMut.mutate({ id: wsDialog.id, name: wsDialog.name.trim() }); }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setWsDialog(null)}>Cancel</Button>
          <Button loading={wsMut.isPending} disabled={!wsDialog?.name.trim()}
            onClick={() => wsMut.mutate({ id: wsDialog!.id, name: wsDialog!.name.trim() })}>
            {wsDialog?.id ? 'Rename' : 'Create'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
